import type { 
  BrandService, 
  CreateBrandServiceData, 
  UpdateBrandServiceData, 
  AssignServiceToBrandData,
  BrandServiceQueryParams,
  Service
} from 'shared/types';
import api from '../axios';

// Brand Services Service Class
export class BrandServicesService {
  // Brand Service CRUD Operations
  async createBrandService(data: CreateBrandServiceData): Promise<BrandService> {
    const response = await api.post<BrandService>('/brand-services', data);
    return response.data;
  }

  async getBrandServices(params?: BrandServiceQueryParams): Promise<BrandService[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.brandId) {
      queryParams.append('brandId', params.brandId);
    }
    if (params?.serviceId) {
      queryParams.append('serviceId', params.serviceId);
    }
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }

    const url = `/brand-services${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<BrandService[]>(url);
    return response.data;
  }

  async getBrandServicesByBrand(brandId: string): Promise<BrandService[]> {
    const response = await api.get<BrandService[]>(`/brand-services/by-brand/${brandId}`);
    return response.data;
  }

  async getBrandServiceById(id: string): Promise<BrandService> {
    const response = await api.get<BrandService>(`/brand-services/${id}`);
    return response.data;
  }

  async updateBrandService(id: string, data: UpdateBrandServiceData): Promise<BrandService> {
    const response = await api.patch<BrandService>(`/brand-services/${id}`, data);
    return response.data;
  }

  async deleteBrandService(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/brand-services/${id}`);
    return response.data;
  }

  // Service Assignment Operations
  async assignServiceToBrand(data: AssignServiceToBrandData): Promise<BrandService> {
    const response = await api.post<BrandService>('/brand-services/assign', data);
    return response.data;
  }

  async unassignServiceFromBrand(brandId: string, serviceId: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/brand-services/unassign/${brandId}/${serviceId}`);
    return response.data;
  }

  async getAvailableServicesForBrand(brandId: string): Promise<Service[]> {
    const response = await api.get<Service[]>(`/brand-services/available/${brandId}`);
    return response.data;
  }

  async toggleBrandServiceStatus(id: string): Promise<BrandService> {
    const response = await api.post<BrandService>(`/brand-services/${id}/toggle`);
    return response.data;
  }

  // Utility Methods
  async getActiveBrandServices(brandId?: string): Promise<BrandService[]> {
    const params: BrandServiceQueryParams = { isActive: true };
    if (brandId) {
      params.brandId = brandId;
    }
    return this.getBrandServices(params);
  }

  async getBrandServicesByService(serviceId: string): Promise<BrandService[]> {
    return this.getBrandServices({ serviceId });
  }

  async checkServiceAvailabilityForBrand(brandId: string, serviceId: string): Promise<boolean> {
    try {
      const response = await api.get<{ available: boolean }>(`/brand-services/check-availability/${brandId}/${serviceId}`);
      return response.data.available;
    } catch (error) {
      console.error('Error checking service availability:', error);
      return false;
    }
  }

  // Bulk Operations
  async bulkAssignServicesToBrand(data: {
    brandId: string;
    serviceIds: string[];
    isActive?: boolean;
  }): Promise<BrandService[]> {
    const response = await api.post<BrandService[]>('/brand-services/bulk-assign', data);
    return response.data;
  }

  async bulkUnassignServicesFromBrand(brandId: string, serviceIds: string[]): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/brand-services/bulk-unassign', {
      brandId,
      serviceIds
    });
    return response.data;
  }

  async bulkToggleBrandServices(brandServiceIds: string[], isActive: boolean): Promise<BrandService[]> {
    const response = await api.post<BrandService[]>('/brand-services/bulk-toggle', {
      brandServiceIds,
      isActive
    });
    return response.data;
  }

  // Analysis and Reporting
  async getBrandServiceUsageStats(brandId: string): Promise<{
    totalServices: number;
    activeServices: number;
    inactiveServices: number;
    utilizationRate: number;
  }> {
    const response = await api.get(`/brand-services/stats/${brandId}`);
    return response.data;
  }

  async getServiceDistributionAcrossBrands(serviceId: string): Promise<{
    totalBrands: number;
    activeBrands: number;
    inactiveBrands: number;
    distributionRate: number;
  }> {
    const response = await api.get(`/brand-services/service-distribution/${serviceId}`);
    return response.data;
  }
}

// Create and export singleton instance
export const brandServicesService = new BrandServicesService();

// Export individual methods for convenience
export const {
  createBrandService,
  getBrandServices,
  getBrandServicesByBrand,
  getBrandServiceById,
  updateBrandService,
  deleteBrandService,
  assignServiceToBrand,
  unassignServiceFromBrand,
  getAvailableServicesForBrand,
  toggleBrandServiceStatus,
  getActiveBrandServices,
  getBrandServicesByService,
  checkServiceAvailabilityForBrand,
  bulkAssignServicesToBrand,
  bulkUnassignServicesFromBrand,
  bulkToggleBrandServices,
  getBrandServiceUsageStats,
  getServiceDistributionAcrossBrands
} = brandServicesService;